import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Database, RefreshCw, Play, AlertTriangle, CheckCircle2,
  Loader2, Shield, ExternalLink,
} from "lucide-react";
import AdminAuthGate from "@/components/admin/AdminAuthGate";

// ── AdminSupabasePage ────────────────────────────────────────────────────
//
// Shadow migration control panel. MongoDB stays the source of truth;
// this page lets an admin:
//   • verify Postgres connectivity
//   • bootstrap the xaluca_shadow schema (idempotent DDL)
//   • run a dry-run OR live sync (Mongo → Postgres)
//   • see per-collection row counts side-by-side and detect drift
//
// The app runtime never reads from Postgres — anything that happens
// here is offline / offside data replication.

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const TRACKED_COLLECTIONS = [
  "text_slots",
  "image_slots",
  "slot_defaults",
  "slot_files",
  "curated_galleries",
  "property_galleries",
  "room_galleries",
  "section_galleries",
  "media_assets",
  "media_folders",
];

const fetchStatus = async () => {
  const res = await fetch(`${API_BASE}/api/admin/supabase/status`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
};

const postBootstrap = async () => {
  const res = await fetch(`${API_BASE}/api/admin/supabase/bootstrap`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.detail || `bootstrap failed (${res.status})`);
  return data;
};

const postSync = async ({ dryRun, collection, limit }) => {
  const params = new URLSearchParams();
  params.set("dry_run", dryRun ? "true" : "false");
  if (collection) params.set("collection", collection);
  if (limit) params.set("limit", String(limit));
  const res = await fetch(`${API_BASE}/api/admin/supabase/sync?${params.toString()}`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.detail || `sync failed (${res.status})`);
  return data;
};

const formatNumber = (n) => (n == null || n < 0 ? "—" : n.toLocaleString());

const DriftRow = ({ name, mongoCount, pgCount, isBusy }) => {
  const drift = mongoCount != null && pgCount != null && pgCount >= 0
    ? mongoCount - pgCount : null;
  let badge;
  if (pgCount === -1 || pgCount == null) {
    badge = { label: "Not synced yet", cls: "bg-[#E8DBC4] text-[#5C5248]" };
  } else if (drift === 0) {
    badge = { label: "In sync", cls: "bg-[#3F6E4A] text-[#FDFBF7]" };
  } else if (drift > 0) {
    badge = { label: `+${drift.toLocaleString()} in Mongo`, cls: "bg-[#C16542] text-[#FDFBF7]" };
  } else {
    badge = { label: `${drift.toLocaleString()} extra in PG`, cls: "bg-[#2C2621] text-[#FDFBF7]" };
  }
  return (
    <tr
      className="border-b border-[#E8DBC4] last:border-b-0"
      data-testid={`admin-supabase-row-${name}`}
    >
      <td className="py-3 pl-4 pr-2 font-mono text-sm text-[#2C2621]">{name}</td>
      <td className="py-3 px-2 text-right tabular-nums text-[#2C2621]">
        {formatNumber(mongoCount)}
      </td>
      <td className="py-3 px-2 text-right tabular-nums text-[#2C2621]">
        {formatNumber(pgCount)}
      </td>
      <td className="py-3 px-2">
        <span
          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${badge.cls}`}
          data-testid={`admin-supabase-status-${name}`}
        >
          {isBusy ? "…" : badge.label}
        </span>
      </td>
    </tr>
  );
};

const AdminSupabasePageInner = () => {
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
  });
  const [busy, setBusy] = useState(null); // "bootstrap" | "dry-run" | "sync" | "sync:<coll>"
  const [banner, setBanner] = useState(null); // { kind: "success"|"error", msg }
  const [lastResult, setLastResult] = useState(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetchStatus();
      setState({ loading: false, error: null, data });
    } catch (err) {
      setState({ loading: false, error: String(err.message || err), data: null });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const conn = state.data?.connection;
  const connected = !!conn?.ok;
  const configured = conn?.configured !== false;

  const runBootstrap = async () => {
    if (busy) return;
    setBusy("bootstrap");
    setBanner(null);
    try {
      const out = await postBootstrap();
      setBanner({ kind: "success", msg: `Schema applied. Tables: ${out.tables?.length || 0}` });
      setLastResult({ kind: "bootstrap", out });
      await refresh();
    } catch (err) {
      setBanner({ kind: "error", msg: `Bootstrap failed: ${err.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const runSync = async ({ dryRun, collection, limit }) => {
    if (busy) return;
    setBusy(dryRun ? `dry-run${collection ? ":" + collection : ""}` : `sync${collection ? ":" + collection : ""}`);
    setBanner(null);
    try {
      const out = await postSync({ dryRun, collection, limit });
      const totalUp = (out.collections || []).reduce((s, r) => s + (r.upserted || 0), 0);
      if (out.error) {
        setBanner({ kind: "error", msg: `Sync completed with errors: ${out.error}` });
      } else if (dryRun) {
        setBanner({ kind: "success", msg: `Dry-run finished. ${out.collections?.length || 0} collections analysed.` });
      } else {
        setBanner({ kind: "success", msg: `Live sync finished. ${totalUp.toLocaleString()} rows upserted.` });
      }
      setLastResult({ kind: dryRun ? "dry-run" : "live", out });
      await refresh();
    } catch (err) {
      setBanner({ kind: "error", msg: `Sync failed: ${err.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const rows = useMemo(() => TRACKED_COLLECTIONS.map((name) => ({
    name,
    mongoCount: state.data?.mongo_counts?.[name],
    pgCount: state.data?.postgres_counts?.[name],
  })), [state.data]);

  const totalMongo = rows.reduce((s, r) => s + (r.mongoCount > 0 ? r.mongoCount : 0), 0);
  const totalPg = rows.reduce((s, r) => s + (r.pgCount > 0 ? r.pgCount : 0), 0);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2621]">
      {/* Header */}
      <div className="border-b border-[#E8DBC4] sticky top-0 bg-[#FDFBF7] z-10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm text-[#5C5248] hover:text-[#2C2621]"
            data-testid="admin-supabase-back"
          >
            <ArrowLeft className="w-4 h-4" /> Admin hub
          </Link>
          <div className="flex-1">
            <h1 className="font-serif-x text-2xl leading-none">Supabase — Shadow migration</h1>
            <p className="text-sm text-[#5C5248] mt-1">
              MongoDB stays the source of truth. Postgres is a read-only replica for exploration and future migration readiness.
            </p>
          </div>
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E8DBC4] rounded-full text-sm hover:bg-[#F5EFE1]"
            data-testid="admin-supabase-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${state.loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Connection card */}
        <div className="rounded-2xl border border-[#E8DBC4] bg-white p-6" data-testid="admin-supabase-connection-card">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              connected ? "bg-[#3F6E4A]" : configured ? "bg-[#C16542]" : "bg-[#E8DBC4]"
            }`}>
              {connected
                ? <CheckCircle2 className="w-6 h-6 text-white" />
                : <Database className={`w-6 h-6 ${configured ? "text-white" : "text-[#5C5248]"}`} />}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-[#5C5248]">Connection</div>
              <div className="mt-1 font-serif-x text-xl">
                {state.loading
                  ? "Checking..."
                  : connected
                    ? "Connected to Supabase"
                    : configured
                      ? "Credentials set but connection failed"
                      : "Credentials not configured"}
              </div>
              {conn?.host && (
                <div className="mt-2 text-xs font-mono text-[#5C5248]" data-testid="admin-supabase-host">
                  host: {conn.host}
                </div>
              )}
              {conn?.db_version && (
                <div className="text-xs font-mono text-[#5C5248]" data-testid="admin-supabase-db-version">
                  {conn.db_version.split(" ").slice(0, 2).join(" ")}
                </div>
              )}
              {conn?.error && (
                <div className="mt-2 rounded-md bg-[#FDECE5] border border-[#F0B99E] px-3 py-2 text-xs text-[#8A3A1D]" data-testid="admin-supabase-conn-error">
                  {conn.error}
                </div>
              )}
              {!configured && (
                <div className="mt-3 rounded-md bg-[#FFF8E1] border border-[#F5D680] px-3 py-2 text-xs text-[#7A5A00] flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    Add <span className="font-mono">SUPABASE_URL</span>, <span className="font-mono">SUPABASE_SERVICE_KEY</span>, and <span className="font-mono">SUPABASE_DB_URL</span> (Transaction Pooler, port 6543) via <span className="font-mono">Publishing → Secrets</span>, then re-publish.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Banner */}
        {banner && (
          <div
            className={`rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${
              banner.kind === "success"
                ? "bg-[#E7F1E7] border-[#3F6E4A] text-[#264C29]"
                : "bg-[#FDECE5] border-[#C16542] text-[#8A3A1D]"
            }`}
            data-testid={`admin-supabase-banner-${banner.kind}`}
          >
            {banner.kind === "success"
              ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
            <div className="flex-1">{banner.msg}</div>
          </div>
        )}

        {/* Actions */}
        <div className="rounded-2xl border border-[#E8DBC4] bg-white p-6" data-testid="admin-supabase-actions">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#5C5248]" />
            <h2 className="font-serif-x text-lg">Actions</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <button
              onClick={runBootstrap}
              disabled={!connected || busy != null}
              className="rounded-xl border border-[#E8DBC4] px-4 py-3 text-left hover:bg-[#F5EFE1] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-supabase-bootstrap"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-[#2C2621]">
                {busy === "bootstrap" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                Bootstrap schema
              </div>
              <div className="text-xs text-[#5C5248] mt-1">
                Idempotent CREATE TABLE IF NOT EXISTS on <span className="font-mono">xaluca_shadow.*</span>.
              </div>
            </button>
            <button
              onClick={() => runSync({ dryRun: true })}
              disabled={!connected || busy != null}
              className="rounded-xl border border-[#E8DBC4] px-4 py-3 text-left hover:bg-[#F5EFE1] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-supabase-dry-run"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-[#2C2621]">
                {busy?.startsWith("dry-run") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Run dry-run
              </div>
              <div className="text-xs text-[#5C5248] mt-1">
                Reports Mongo vs PG counts. Zero writes.
              </div>
            </button>
            <button
              onClick={() => {
                if (!window.confirm("This will UPSERT thousands of rows into Supabase (xaluca_shadow.*). MongoDB is not touched. Continue?")) return;
                runSync({ dryRun: false });
              }}
              disabled={!connected || busy != null}
              className="rounded-xl border-2 border-[#C16542] px-4 py-3 text-left hover:bg-[#FDECE5] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-supabase-sync"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-[#C16542]">
                {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Run live sync
              </div>
              <div className="text-xs text-[#5C5248] mt-1">
                UPSERT every tracked collection Mongo → PG.
              </div>
            </button>
          </div>
        </div>

        {/* Drift table */}
        <div className="rounded-2xl border border-[#E8DBC4] bg-white overflow-hidden" data-testid="admin-supabase-drift-table">
          <div className="px-6 py-4 border-b border-[#E8DBC4] flex items-center gap-3">
            <h2 className="font-serif-x text-lg flex-1">Collections</h2>
            <span className="text-xs text-[#5C5248]" data-testid="admin-supabase-totals">
              Mongo: {formatNumber(totalMongo)} · Postgres: {formatNumber(totalPg)}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE1] text-[10px] uppercase tracking-widest text-[#5C5248]">
              <tr>
                <th className="text-left py-2 pl-4 pr-2 font-semibold">Collection</th>
                <th className="text-right py-2 px-2 font-semibold">Mongo</th>
                <th className="text-right py-2 px-2 font-semibold">Postgres</th>
                <th className="text-left py-2 px-2 font-semibold">Status</th>
                <th className="text-right py-2 pr-4 font-semibold">Per-collection sync</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.name}>
                  <DriftRow {...r} isBusy={busy && busy.endsWith(":" + r.name)} />
                  <tr className="border-b border-[#E8DBC4] last:border-b-0" data-testid={`admin-supabase-actions-${r.name}`}>
                    <td colSpan={4}></td>
                    <td className="py-1 pr-4 text-right">
                      <button
                        onClick={() => runSync({ dryRun: false, collection: r.name })}
                        disabled={!connected || busy != null}
                        className="text-xs px-2 py-1 rounded-full border border-[#E8DBC4] hover:bg-[#F5EFE1] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`admin-supabase-sync-${r.name}`}
                      >
                        Sync only this
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recent runs */}
        {state.data?.recent_runs?.length > 0 && (
          <div className="rounded-2xl border border-[#E8DBC4] bg-white overflow-hidden" data-testid="admin-supabase-recent-runs">
            <div className="px-6 py-4 border-b border-[#E8DBC4]">
              <h2 className="font-serif-x text-lg">Recent runs</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F5EFE1] text-[10px] uppercase tracking-widest text-[#5C5248]">
                <tr>
                  <th className="text-left py-2 pl-4 pr-2 font-semibold">Started</th>
                  <th className="text-left py-2 px-2 font-semibold">Mode</th>
                  <th className="text-left py-2 px-2 font-semibold">Scope</th>
                  <th className="text-left py-2 pr-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {state.data.recent_runs.map((run) => (
                  <tr key={run.id} className="border-b border-[#E8DBC4] last:border-b-0">
                    <td className="py-2 pl-4 pr-2 font-mono text-xs">{run.started_at?.replace("T", " ").slice(0, 19) || ""}</td>
                    <td className="py-2 px-2">{run.mode}</td>
                    <td className="py-2 px-2 text-xs text-[#5C5248]">{run.scope}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${
                        run.status === "success" ? "bg-[#3F6E4A] text-white"
                          : run.status === "error" ? "bg-[#C16542] text-white"
                            : "bg-[#E8DBC4] text-[#5C5248]"
                      }`}>{run.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Last result JSON */}
        {lastResult && (
          <details className="rounded-2xl border border-[#E8DBC4] bg-white overflow-hidden" data-testid="admin-supabase-last-result">
            <summary className="px-6 py-4 cursor-pointer flex items-center gap-2 text-sm font-semibold">
              <ExternalLink className="w-4 h-4" />
              Last result payload
            </summary>
            <pre className="px-6 py-4 bg-[#F9F5EC] text-xs font-mono text-[#2C2621] overflow-auto max-h-96 whitespace-pre-wrap break-words">
              {JSON.stringify(lastResult.out, null, 2)}
            </pre>
          </details>
        )}

        {state.error && (
          <div className="rounded-xl border border-[#C16542] bg-[#FDECE5] px-4 py-3 text-sm text-[#8A3A1D]" data-testid="admin-supabase-page-error">
            Could not load status: {state.error}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminSupabasePage = () => (
  <AdminAuthGate>
    <AdminSupabasePageInner />
  </AdminAuthGate>
);

export default AdminSupabasePage;
